/**
 * Business settings — docs/11 §Setup → Settings.
 *
 * Every one of these is a switch the business throws, not a deployment
 * decision, so each carries its own explanation in the words the operator
 * reads. The registry is the single list: the screen renders it, the server
 * validates against it, and `effect` records where in the code the setting is
 * actually honoured — a toggle that changes nothing is worse than no toggle.
 */

export type SettingKind = 'boolean' | 'number' | 'text' | 'select' | 'lookup'

export interface SettingDef {
  readonly key: string
  readonly group: string
  readonly label: string
  /** The paragraph under the switch, in the business's own terms. */
  readonly blurb: string
  readonly kind: SettingKind
  readonly defaultValue: boolean | number | string
  readonly options?: ReadonlyArray<{ value: string; label: string }>
  /** `valueField: 'code'` stores the record's code rather than its id. */
  readonly lookup?: { entity: string; valueField?: 'id' | 'code' }
  /**
   * Once on, it stays on (SET-005): "Start using inventory" is a decision a
   * company takes once, dated, and projects created after it work with stock.
   */
  readonly oneWay?: boolean
  /**
   * A setting that cannot be taken back once given — the observed system says
   * so in as many words about quantity precision.
   */
  readonly irreversible?: boolean
  /** Where this is honoured. Empty means nothing reads it yet. */
  readonly effect: string
  /**
   * Shown indented under this boolean, and only while it is on — the
   * reference nests the site-inventory and purchase-plan options under Start
   * using inventory (SET1-018).
   */
  readonly parent?: string
}

const def = <const T extends SettingDef>(s: T): T => s

export const SETTINGS = [
  /*
    The reference's Settings card, in its order (SET1-018): the eighteen
    settings it shows come first, in one General group, with the two
    inventory options nested under Start using inventory and the allowed
    networks under the IP restriction. What this system adds follows in
    groups of its own.
  */

  def({
    key: 'inventory_enabled',
    group: 'General',
    label: 'Start using inventory',
    blurb: 'You can start managing inventory. Applicable only to the new projects.',
    kind: 'boolean',
    defaultValue: true,
    oneWay: true,
    effect: 'The inventory document types appear on the Transactions hub, and projects created from now on are inventory projects.',
  }),
  def({
    key: 'site_inventory_enabled',
    group: 'General',
    label: 'Enable project site inventory management',
    blurb: 'Projects can have site store and you can check the inventory in the store. Material in and out of site store will be recorded.',
    kind: 'boolean',
    parent: 'inventory_enabled',
    defaultValue: true,
    effect: 'Site inventory records and material transfers to site appear on the Transactions hub.',
  }),
  def({
    key: 'purchase_plan_in_material_request',
    group: 'General',
    label: "Enable 'Set up purchase plan' option in material request",
    blurb: 'Based on the stock available in warehouse the stores team can decide how much has to be purchased for a material request.',
    kind: 'boolean',
    parent: 'inventory_enabled',
    defaultValue: false,
    effect: 'An approved material request joins the purchase-plan work list until a plan is set, and no purchase order may be raised from it before then.',
  }),
  def({
    key: 'default_labour_item',
    group: 'General',
    label: 'Default Labour item for Installation',
    blurb: 'When you add a "Material and Labour" cost line in the estimation, the Labour Item configured in the Item Category of the material is used. If the labour item is not configured for an Item Category, this default item will be used.',
    kind: 'lookup',
    defaultValue: '',
    lookup: { entity: 'item' },
    effect: "A BOQ import's labour leg falls back to this item when neither the sheet nor the material's category names one.",
  }),

  def({
    key: 'quantity_decimals',
    group: 'General',
    label: 'Current decimal precision for quantities',
    blurb: 'You can set the decimal precision for quantities, default is 2 digits. You can optionally change it to 3 digits (the change is irreversible).',
    kind: 'select',
    defaultValue: '3',
    options: [{ value: '2', label: '2 decimals' }, { value: '3', label: '3 decimals' }],
    irreversible: true,
    effect: 'Quantities are rounded and displayed to this precision.',
  }),
  def({
    key: 'rfq_enabled',
    group: 'General',
    label: 'Enable RFQ',
    blurb: 'You can issue RFQs and get response from your supplier via portal (online). No need to manually record supplier quotations. Comparing quotations and taking decision is easy and helps you at the time of purchase with relevant information.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Requests for quote and supplier quotations appear on the Transactions hub.',
  }),
  def({
    key: 'show_estimated_rate_to_purchase_team',
    group: 'General',
    label: 'Show estimated rate to purchase team',
    blurb: 'When this option is enabled, estimated rate will be visible to the purchase team while creating a PO/Subcontract Order. When this option is not enabled, only approvers can see the estimated rate in PO/Subcontract Order.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'The estimated rate on pulled source lines is withheld from users who cannot approve.',
  }),

  def({
    key: 'manual_numbering_projects',
    group: 'General',
    label: 'Enable manual document numbering for Projects',
    blurb: 'When this option is enabled, you can skip the numbering rules and manually enter an project number when required.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project can be submitted with a number you typed instead of the next in the series.',
  }),
  def({
    key: 'manual_numbering_invoices',
    group: 'General',
    label: 'Enable manual document numbering for Sales Invoices and Credit Notes',
    blurb: 'When this option is enabled, you can skip the numbering rules and manually enter an invoice or credit note number when required.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A sales invoice or credit note can be submitted with a number you typed.',
  }),
  def({
    key: 'line_discount_enabled',
    group: 'General',
    label: 'Enable discount option in Projects and Sales Invoices',
    blurb: 'When this option is enabled, you can enter discount % on BOQ rate in Project and Sales Invoices.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'The discount column is shown on line grids.',
  }),
  def({
    key: 'overall_discount_enabled',
    group: 'General',
    label: 'Enable Overall Discount',
    blurb: 'You will have the option to use overall discount instead of line-item level discount. If you enable this setting, make sure your custom print formats are updated.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A document-level discount is accepted and shown in the totals.',
  }),
  // SET1-021 — the switch; the allow-list is nested under it.
  def({
    key: 'ip_restriction_enabled',
    group: 'General',
    label: 'Enable IP based account access restriction',
    blurb: 'You can restrict your users to access this system only from approved office networks. You need static IP address for your office internet connection to use this feature.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'With this on, the networks listed under it are the only ones a sign-in or a request is accepted from.',
  }),
  def({
    key: 'ip_allow_list',
    group: 'General',
    label: 'Allowed networks',
    blurb:
      'Comma-separated IPv4 addresses or CIDR ranges. You need a static address at the office to use this.',
    kind: 'text',
    parent: 'ip_restriction_enabled',
    defaultValue: '',
    effect: 'A signed-in request from outside the list is refused.',
  }),
  def({
    key: 'allow_excess_purchase',
    group: 'General',
    label: 'Allow excess quantity in purchase',
    blurb: 'For materials that have minimum order quantity (MOQ) while purchasing, you can allow PO for more quantities than MR quantities. You need to enable this setting also at the Item category level. If you enable this setting, we strongly recommend that you set up an extra approval step for excess quantity purchase.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Excess purchase quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_receipt',
    group: 'General',
    label: 'Allow excess quantity in Receipt Note (GRN)',
    blurb: 'You can receive material excess than the ordered quantity. Set the limit in the respective item category. If you receive more than the limit you can set special approval for the receipt note (GRN).',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Excess receipt quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_work_progress',
    group: 'General',
    label: 'Allow excess quantity in Work Progress',
    blurb: 'You can do work progress more than the order quantity. If you enable this setting, we strongly recommend that you set up an extra approval step for excess quantity work progress.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Recorded progress beyond the ordered quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'default_progress_method',
    group: 'General',
    label: 'Default BOQ progress calculation method',
    blurb: 'When a BOQ line has multiple billable estimated lines, select the "default" method to be used for calculating progress for billing reconciliation. You can still choose/change the required method for each project/scope of work.',
    kind: 'select',
    defaultValue: 'CUMULATIVE',
    options: [
      { value: 'CUMULATIVE', label: 'Cumulative — add every line up' },
      { value: 'MINIMUM', label: 'Minimum — the least complete line governs' },
    ],
    effect: 'Billing reconciliation uses this to turn estimation progress into BOQ progress.',
  }),

  def({
    key: 'einvoice_enabled',
    group: 'General',
    label: 'Enable E-Invoice Integration',
    blurb: 'You can generate e-invoice through integration with Invoice Registration Portal (IRP).',
    kind: 'boolean',
    defaultValue: false,
    // Registration itself is not built: there is no IRP client and no queue,
    // so the effect says only what actually happens. A setting's `effect` is
    // the record of where it is honoured, and one that describes a feature
    // nobody wrote is how a gap goes unnoticed.
    effect: 'The e-invoice acknowledgement fields are shown on a sales invoice.',
  }),
  def({
    key: 'ewaybill_enabled',
    group: 'General',
    label: 'Enable E-Way Bill Integration',
    blurb: 'You can generate E-Way Bill through integration with E-Way Bill Portal.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'The e-way bill number and status are shown on delivery challans and material transfers.',
  }),
  /*
    The connector itself is NOT a setting.

    `accounting_connector` and `integration_from_date` used to live here and be
    read by nothing: the live connection — which connector, from what date,
    with what credentials and over which document types — is configured on the
    Integration page and stored in `integration_config`. Two places to answer
    one question meant the Settings screen could say "Zoho Books" while nothing
    was connected. There is now one.
  */
  def({
    key: 'enforce_password_rules',
    group: 'General',
    label: 'Enforce password rules',
    blurb: 'When enabled, passwords must be at least 8 characters long, and include one uppercase letter, one lowercase letter, one special character, and one number.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Password strength is checked when a password is set or changed.',
  }),

  def({
    key: 'stock_count_full_days',
    group: 'Inventory',
    label: 'Count every store in full every … days',
    blurb: 'How often a full stock audit is due for each warehouse and site store. 30 is monthly; 7 is weekly.',
    kind: 'number',
    defaultValue: 30,
    effect: 'The stock audit status report shows a store as due once its last full count is this old.',
  }),
  def({
    key: 'stock_count_a_days',
    group: 'Inventory',
    label: 'Cycle-count class A every … days',
    blurb: 'High-value materials are counted more often than the rest. Weekly is usual.',
    kind: 'number',
    defaultValue: 7,
    effect: 'The stock audit status report shows class A as due once its last count is this old.',
  }),
  def({
    key: 'stock_count_b_days',
    group: 'Inventory',
    label: 'Cycle-count class B every … days',
    blurb: 'Valves and pipes, the middle of the range: monthly is usual.',
    kind: 'number',
    defaultValue: 30,
    effect: 'The stock audit status report shows class B as due once its last count is this old.',
  }),
  def({
    key: 'stock_count_c_days',
    group: 'Inventory',
    label: 'Cycle-count class C every … days',
    blurb: 'Fittings and consumables: quarterly is usual.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The stock audit status report shows class C as due once its last count is this old.',
  }),

  def({
    key: 'auto_pick_stock',
    group: 'Inventory',
    label: 'Pick stock automatically when a line is typed',
    blurb: 'An issue, transfer, challan or decrease typed without picking a lot takes the oldest lots in that store — this job’s stock first, then free stock, never another job’s. One line per lot.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Stock goes down when material is issued, even if nobody used “Pick from stock”. Off: such a line moves no stock, as before.',
  }),

  /* ---- material planning (docs/24 §3.2) ------------------------------ */
  def({
    key: 'abc_a_value_share',
    group: 'Material planning',
    label: 'Class A covers the first … % of value',
    blurb:
      'Rank the materials by value, most expensive first. Those making up this share of the total are class A — high value.',
    kind: 'number',
    defaultValue: 70,
    effect: 'The ABC analysis report suggests class A up to this cumulative share.',
  }),
  def({
    key: 'abc_b_value_share',
    group: 'Material planning',
    label: 'Class B runs on to … % of value',
    blurb: 'After class A, the materials up to this cumulative share are class B. Everything after is class C.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The ABC analysis report suggests class B up to this cumulative share.',
  }),
  def({
    key: 'abc_a_lead_days',
    group: 'Material planning',
    label: 'A lead time of … days or more makes a material class A',
    blurb:
      'A water tank that takes two months to make is class A even if it is not expensive: it has to be ordered first.',
    kind: 'number',
    defaultValue: 45,
    effect: 'The ABC analysis report lifts a material to class A when its lead time is this long.',
  }),
  def({
    key: 'abc_b_lead_days',
    group: 'Material planning',
    label: 'A lead time of … days or more makes a material at least class B',
    blurb: 'Valves and pipes that take a fortnight to arrive are class B whatever they cost.',
    kind: 'number',
    defaultValue: 10,
    effect: 'The ABC analysis report lifts a material to class B when its lead time is this long.',
  }),
  def({
    key: 'mrp_buffer_days',
    group: 'Material planning',
    label: 'Order … days earlier than the lead time alone says',
    blurb:
      'A margin for approvals, transport and the unexpected. The requirement plan orders this many days before need-by less lead time.',
    kind: 'number',
    defaultValue: 3,
    effect: 'The material requirement plan subtracts it, with the lead time, from each need-by date.',
  }),
  def({
    key: 'lead_time_a_days',
    group: 'Material planning',
    label: 'Lead time for class A, in days',
    blurb: 'Used for a class A material when neither the item nor its category gives a lead time of its own.',
    kind: 'number',
    defaultValue: 75,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_b_days',
    group: 'Material planning',
    label: 'Lead time for class B, in days',
    blurb: 'Used for a class B material when neither the item nor its category gives a lead time of its own.',
    kind: 'number',
    defaultValue: 20,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_c_days',
    group: 'Material planning',
    label: 'Lead time for class C, in days',
    blurb: 'Fittings and consumables, bought locally. Used when neither the item nor its category says.',
    kind: 'number',
    defaultValue: 3,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_s_days',
    group: 'Material planning',
    label: 'Lead time for class S, in days',
    blurb: 'Special and customised materials: made to the customer’s specification, so usually the longest wait.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),

  /* ---- vendor selection (docs/24 §3.5) ------------------------------- */
  def({
    key: 'cs_price_weight_pct',
    group: 'Vendor selection',
    label: 'How much of the comparison is price, in %',
    blurb:
      'The comparative statement scores each quotation out of 100: this share on price, the rest on '
      + 'the supplier’s latest evaluation — quality, delivery, compliance and the rest. 100 compares on price alone.',
    kind: 'number',
    defaultValue: 70,
    effect: 'The comparative statement weighs price and vendor score by it, and recommends the highest.',
  }),

  /* ---- what the rules allow ------------------------------------------ */
  def({
    key: 'escalate_exceptions_to_director',
    group: 'What the rules allow',
    label: 'Send overridden exceptions to a director',
    blurb:
      'When somebody waves past an excess quantity or buys a non-tendered item, add a final '
      + 'approval stage so a director sees it before the document is live. Without this, the '
      + 'person raising the document is also the person who signs for the exception.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'An overridden exception adds a "Director — exception approval" stage to the workflow.',
  }),
  def({
    key: 'exception_approver_role',
    group: 'What the rules allow',
    label: 'Who signs for an overridden exception',
    blurb:
      'The role the exception stage is assigned to. Only people holding it may sign that step. '
      + 'Left empty, anyone who may approve can — which is the whole point of the stage lost, so name one.',
    kind: 'lookup',
    defaultValue: '',
    lookup: { entity: 'app_role', valueField: 'code' },
    effect: 'The "Director — exception approval" stage is bound to the employees holding this role.',
  }),
  def({
    key: 'require_material_request_for_po',
    group: 'What the rules allow',
    label: 'A purchase order must come from a material request',
    blurb:
      'Refuse a purchase order line that nobody asked for. Buying without a request is how a '
      + 'project ends up with material it did not need, which is the leak this system exists to '
      + 'close. Stock and tool orders are unaffected.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project purchase order with a line raised from nothing is refused.',
  }),
  def({
    key: 'require_requisition_for_po',
    group: 'What the rules allow',
    label: 'A purchase order must come from a purchase requisition',
    blurb:
      'Buy only what the purchase executive has requisitioned. The site asks with a material '
      + 'request; purchasing decides with a requisition; the order carries that decision out. '
      + 'Stock and tool orders are unaffected.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project purchase order with a line that is not on a purchase requisition is refused.',
  }),
  def({
    key: 'grn_inspection_required',
    group: 'What the rules allow',
    label: 'Every receipt note must be inspected',
    blurb:
      'Material is inspected at the gate before it enters stock: who inspected it is named on the receipt, '
      + 'and a receipt nobody inspected needs an approval of its own.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A receipt note with no inspector named raises an exception that has to be approved.',
  }),
  def({
    key: 'non_tendered_needs_approval',
    group: 'What the rules allow',
    label: 'Non-tendered work needs approval before it is bought',
    blurb:
      'A BOQ line marked NT is work outside the original tender. With this on, ordering against '
      + 'one is an exception somebody with authority has to sign for.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'Ordering against a non-tendered BOQ line raises an overridable exception.',
  }),

  def({
    key: 'allow_excess_billing',
    group: 'What the rules allow',
    label: 'Allow billing beyond the BOQ quantity',
    blurb:
      'Bill a customer for more than the BOQ line ordered. Where this is off the excess is refused outright; where it is on it is an exception somebody with authority signs for.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Billing beyond the BOQ order quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_challan',
    group: 'What the rules allow',
    label: 'Allow a challan beyond what was ordered',
    blurb: 'Send out more than the order or transfer behind the challan accounted for.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'An excess challan quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'separate_submitter_and_approver',
    group: 'Access',
    label: 'The person who submits a document may not approve it',
    blurb:
      'Segregation of duties. With this off, one administrator can raise a purchase order, wave past '
      + 'an excess-quantity exception and approve the result, and the audit trail shows one name at '
      + 'every step. Switch it off only where there is genuinely nobody else to sign.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Approving a document you submitted yourself is refused.',
  }),
  def({
    key: 'management_contact_email',
    group: 'Access',
    label: 'Management contact for data exports',
    blurb:
      'The password of a whole-company data export is emailed here, not to whoever asked for the export. '
      + 'Left empty, it goes to the person who asked.',
    kind: 'text',
    defaultValue: '',
    effect: 'The data export password is emailed to this address.',
  }),
] as const satisfies readonly SettingDef[]

export type SettingKey = (typeof SETTINGS)[number]['key']

const byKey = new Map<string, SettingDef>(SETTINGS.map((s) => [s.key as string, s as SettingDef]))

export const getSettingDef = (key: string): SettingDef | undefined => byKey.get(key)

export const SETTING_GROUPS: readonly string[] = [...new Set(SETTINGS.map((s) => s.group))]

/**
 * The allow-list that is in force: the networks listed, while the IP
 * restriction is switched on (SET1-021); nothing otherwise.
 */
export const ipAllowListInForce = (settings: Readonly<Record<string, unknown>>): string =>
  settings['ip_restriction_enabled'] === true ? String(settings['ip_allow_list'] ?? '') : ''

/** Every setting at its default, which is what an untouched tenant has. */
export const defaultSettings = (): Record<string, boolean | number | string> =>
  Object.fromEntries(SETTINGS.map((s) => [s.key, s.defaultValue]))

/** The shape the API answers with, and the client holds. */
export interface SettingsView {
  readonly values: Readonly<Record<string, boolean | number | string>>
  /** When and by whom each changed setting was last set. */
  readonly changed: Readonly<Record<string, { at: string; by: string | null }>>
}
